import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMessageSquare, FiPenTool, FiCode, FiRocket } = FiIcons;

const Process = () => {
  const steps = [
    {
      icon: FiMessageSquare,
      title: 'Discovery & Planning',
      time: '0-8 hours',
      description: 'We understand your vision, define requirements, and create a detailed project roadmap.',
      details: [
        'Stakeholder interviews',
        'Requirements gathering',
        'Technical architecture planning',
        'Timeline and milestone definition'
      ]
    },
    {
      icon: FiPenTool,
      title: 'Design & Prototyping',
      time: '8-24 hours',
      description: 'Create user-centered designs and interactive prototypes for validation.',
      details: [
        'User experience design',
        'Interactive prototypes',
        'Design system creation',
        'Stakeholder feedback integration'
      ]
    },
    {
      icon: FiCode,
      title: 'Development & Testing',
      time: '24-64 hours',
      description: 'Build your MVP with clean, scalable code and comprehensive testing.',
      details: [
        'Agile development sprints',
        'Continuous integration',
        'Quality assurance testing',
        'Performance optimization'
      ]
    },
    {
      icon: FiRocket,
      title: 'Deployment & Launch',
      time: '64-72 hours',
      description: 'Deploy your MVP to production and ensure everything runs smoothly.',
      details: [
        'Production deployment',
        'Performance monitoring',
        'User training and documentation',
        'Post-launch support'
      ]
    }
  ];

  return (
    <section id="process" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Our 72-Hour Process
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A proven methodology that delivers results fast without compromising on quality.
          </p>
        </motion.div>

        <div className="relative">
          {/* Timeline line */}
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-blue-400 to-purple-400 rounded-full"></div>

          <div className="space-y-12">
            {steps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className={`flex flex-col lg:flex-row items-center gap-8 ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
              >
                {/* Content */}
                <div className="flex-1 bg-white rounded-2xl p-8 shadow-lg relative">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mr-4">
                      <SafeIcon icon={step.icon} className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{step.title}</h3>
                      <p className="text-blue-600 font-medium">{step.time}</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-6">{step.description}</p>
                  
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {step.details.map((detail, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-700">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3 flex-shrink-0"></div>
                        {detail}
                      </li>
                    ))}
                  </ul>

                  {/* Arrow for desktop */}
                  <div className={`hidden lg:block absolute top-1/2 transform -translate-y-1/2 ${
                    index % 2 === 0 ? '-right-4' : '-left-4'
                  }`}>
                    <div className={`w-8 h-8 bg-white border-2 border-blue-400 rounded-full transform rotate-45 ${
                      index % 2 === 0 ? 'translate-x-2' : '-translate-x-2'
                    }`}></div>
                  </div>
                </div>

                {/* Timeline dot */}
                <div className="hidden lg:block w-4 h-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full border-4 border-white shadow-lg z-10"></div>

                {/* Spacer for alternating layout */}
                <div className="flex-1 hidden lg:block"></div>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-white rounded-2xl p-8 shadow-lg max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Why Our Process Works
            </h3>
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-600">⚡</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Speed</h4>
                <p className="text-gray-600 text-sm">Rapid development without cutting corners</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-600">🎯</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Focus</h4>
                <p className="text-gray-600 text-sm">Concentrated effort on essential features</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">✓</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Quality</h4>
                <p className="text-gray-600 text-sm">Rigorous testing and quality assurance</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Process;